import app from 'durandal/app';
import $ from 'jquery';
import { throttle } from 'lodash-es';

import { ApiService } from '@/features/api';
import SessionTimeout from '@/legacy/modals/sessionTimeout';
import * as userService from '@/legacy/services/user';

/** Time to wait before presenting a session reactivation dialog. */
const SESSION_TIMEOUT = 15 * 60 * 1000;

/** Time to wait for session reactivation before automatically killing it. */
const SESSION_KILL = 5 * 60 * 1000;

/** Time between user activity pings (mouse movement, etc.). */
const PING_INTERVAL = 3 * 60 * 1000;

class SessionTimeoutService {
  private sessionTimeout: ReturnType<typeof setTimeout>;

  private sessionKill: ReturnType<typeof setTimeout>;

  constructor() {
    // To avoid creating a circular dependency chain between this and UserService (and the dialog
    // viewmodel), expose the public methods to the Durandal event bus so that indirect calls can be
    // made after module initialization.
    app.on('user:keepSessionAlive').then(this.keepSessionAlive);
    app.on('user:resetSessionTimeout').then(this.resetSessionTimeout);
    app.on('user:showSessionTimeout').then(this.showTimeout);
    $(window).on('focus resize mousemove keyup', this.keepSessionAlive);
  }

  private keepSessionAlive = throttle(() => {
    if (userService.Instance.IsAuthenticated() && !SessionTimeout.isDisplayed) {
      this.resetSessionTimeout();
      this.ping();
    }
  }, PING_INTERVAL);

  private setLastSessionActionToNow() {
    try {
      localStorage.setItem('lastSessionAction', Date.now().toString());
    } catch {
      // do nothing
    }
  }

  private getLastSessionAction() {
    try {
      const valueAsString = localStorage.getItem('lastSessionAction');
      const value = valueAsString ? Number.parseInt(valueAsString, 10) : Number.NaN;
      return Number.isNaN(value) ? null : value;
    } catch {
      return null;
    }
  }

  private getTimeSinceLastSession() {
    const lastSessionAction = this.getLastSessionAction();
    return lastSessionAction != null ? Date.now() - lastSessionAction : null;
  }

  private disconnectSession() {
    userService.Instance.clearAuthenticationToken();

    if (!userService.Instance.IsAuthenticated()) {
      return;
    }

    this.showTimeout();

    // force session termination if timer runs out
    const resetSessionKill = (waitMs: number) => {
      if (this.sessionKill != null) {
        clearTimeout(this.sessionKill);
        this.sessionKill = undefined;
      }

      this.sessionKill = setTimeout(() => {
        this.sessionKill = undefined;

        // Ensure user didn't interact with any other Plooto windows. If they did, keep this session
        // alive.
        const timeSinceLastSession = this.getTimeSinceLastSession();
        if (timeSinceLastSession != null) {
          const nextWaitMs = SESSION_TIMEOUT + SESSION_KILL - timeSinceLastSession;
          if (nextWaitMs > 0) {
            resetSessionKill(nextWaitMs);
            return;
          }
        }

        // check if connection is still available
        userService.Instance.Logout();
      }, waitMs);
    };

    resetSessionKill(SESSION_KILL);
  }

  private resetSessionTimeout = () => {
    // Only set last action in localStorage if user has a valid token. We'll use this timestamp for
    // cross-window sessions.
    if (userService.Instance.hasAuthenticationToken()) {
      this.setLastSessionActionToNow();
    }

    if (this.sessionKill != null) {
      clearTimeout(this.sessionKill);
      this.sessionKill = undefined;
    }

    const startSessionTimeout = (waitMs: number) => {
      if (this.sessionTimeout != null) {
        clearTimeout(this.sessionTimeout);
        this.sessionTimeout = undefined;
      }

      this.sessionTimeout = setTimeout(() => {
        this.sessionTimeout = undefined;

        // Ensure user didn't interact with any other Plooto windows. If they did, keep this session
        // alive.
        const timeSinceLastSession = this.getTimeSinceLastSession();
        if (timeSinceLastSession != null) {
          const nextWaitMs = SESSION_TIMEOUT - timeSinceLastSession;
          if (nextWaitMs > 0) {
            startSessionTimeout(nextWaitMs);
            return;
          }
        }

        this.disconnectSession();
      }, waitMs);
    };

    startSessionTimeout(SESSION_TIMEOUT);
  };

  public showTimeout = (eventArgs?: { reloadAfterAuthenticate?: boolean }) =>
    SessionTimeout.show(userService.Instance.DisplayEmail()).then((didAuthenticate) => {
      if (didAuthenticate && eventArgs?.reloadAfterAuthenticate === true) {
        window.location.reload();
      }
      return didAuthenticate;
    });

  private ping(): void {
    // XXX: Ideally, we would await this call and trigger the session timeout behaviour if the
    // session expired server-side for whatever reason (e.g. the tab was suspended due to sleep
    // mode).
    ApiService.post(`${import.meta.env.APP_URLS_API_URL}/v1/user/ping`);
  }
}

const Instance = new SessionTimeoutService();

export { Instance as SessionTimeoutService };
